import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useApi from "../../../../../utils/api";
import { useCompanyDateFormat } from "../../../../../utils/hooks/company";
import { dateTimeFormat } from "../../../../../utils/helpers/date";
import { getToastErrorMessage } from "../../../../../utils/helpers/errors";
import { useDeleteModal } from "../../../helpers/useDeleteModal";

// The calls behind the versions of a time off plan, for any plan id:
// `fetchVersions` (newest first, deleted ones left out) and `discardVersion` (DELETE the pending
// version, then a toast; resolves true when it was discarded).
export const useTimeOffPlanVersionsApi = () => {
    const { t } = useTranslation();
    const { authGet, authDelete } = useApi();

    const fetchVersions = useCallback(async (planId, signal) => {
        const response = await authGet(`/timeoff/${planId}/versions`, {
            params: { pagination: false },
            signal,
        });
        return (response?.result || [])
            .filter((version) => version.status !== "deleted")
            .sort((a, b) => b.startEffectiveDate.localeCompare(a.startEffectiveDate));
    }, [authGet]);

    const discardVersion = useCallback(async (planId, version) => {
        try {
            await authDelete(`/timeoff/${planId}/versions/${version.id}`);
            toast.success(t("time-off-plan-scheduled-change-discarded"));
            return true;
        } catch (err) {
            toast.error(getToastErrorMessage(err));
            return false;
        }
    }, [authDelete, t]);

    return { fetchVersions, discardVersion };
};

// The versions of a time off plan (newest first, deleted ones left out) and the Discard action
// for the scheduled (pending) one: a confirmation modal, then DELETE, a toast and a reload.
// `onDiscarded` runs after a discard (e.g. to reload the plan list and its badge).
export const useTimeOffPlanVersions = (planId, onDiscarded) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const { fetchVersions, discardVersion } = useTimeOffPlanVersionsApi();
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    const reload = useCallback(() => setReloadKey((prev) => prev + 1), []);

    useEffect(() => {
        if (!planId) {
            setVersions([]);
            return;
        }
        const controller = new AbortController();
        setLoading(true);
        fetchVersions(planId, controller.signal)
            .then(setVersions)
            .catch(() => setVersions([]))
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });
        return () => controller.abort();
    }, [fetchVersions, planId, reloadKey]);

    const discard = useCallback(async (version) => {
        if (await discardVersion(planId, version)) {
            reload();
            onDiscarded?.();
        }
    }, [discardVersion, planId, reload, onDiscarded]);

    const { open: openDiscardModal, modal: discardModal } = useDeleteModal({
        message: (version) => t("time-off-plan-discard-scheduled-change-confirm", {
            date: dateTimeFormat(version?.startEffectiveDate, dateFormat),
        }),
        onConfirm: discard,
    });

    return { versions, loading, reload, openDiscardModal, discardModal };
};
